import * as React from 'react'

import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import SuperFilter from '~/client/src/desktop/components/SuperFilter/SuperFilter'
import ViewModes from '~/client/src/desktop/enums/ViewModes'
import DesktopEventStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import DynamicOverflowList from '~/client/src/shared/components/DynamicOverflowList/DynamicOverflowList'
import DynamicOverflowListStore from '~/client/src/shared/components/DynamicOverflowList/DynamicOverflowList.store'
import FilterResetter from '~/client/src/shared/components/FilterResetter/FilterResetter'
import FacetButton from '~/client/src/shared/components/Filters/components/FacetButton/FacetButton'
import SuperFilterHandleButton from '~/client/src/shared/components/Filters/SuperFilter/SuperFilterHandleButton'
import MenuCloser from '~/client/src/shared/components/MenuCloser'
import { RESET_ALL_FILTERS } from '~/client/src/shared/stores/EventStore/eventConstants'
import EventContext from '~/client/src/shared/stores/EventStore/EventContext'
import { IFilters } from '~/client/src/shared/stores/InitialState'
import LogisticsFilterStore from '~/client/src/shared/stores/LogisticsFilter.store'
import { NOOP } from '~/client/src/shared/utils/noop'

interface IProps {
  store: LogisticsFilterStore
  forceCloseMap: { [filterType: string]: boolean }
  eventsStore: DesktopEventStore
  filters: IFilters
  dynamicOverflowListStore: DynamicOverflowListStore
  viewMode: ViewModes

  onResetClick?(): void
}

const MORE_BTN_WIDTH = 55
const MIN_CONTAINER_WIDTH = 200

@observer
export default class LogisticsFilter extends React.Component<IProps> {
  @observable private isOverflowedModalShown: boolean = false
  @observable private localProps: IProps = null
  private readonly clearPostEventCallback: () => void = NOOP

  public constructor(props: IProps) {
    super(props)

    this.localProps = props
    props.store.resetAllFilters()

    this.clearPostEventCallback = props.eventsStore.addPostEventCallback(
      this.handleDesktopPostEventCallback,
    )
    makeObservable(this)
  }

  public componentDidUpdate(prevProps: IProps) {
    const {
      forceCloseMap,
      filters: { fieldsMap },
      viewMode,
      dynamicOverflowListStore,
    } = this.props

    runInAction(() => {
      if (prevProps !== this.props) {
        this.localProps = this.props
      }

      if (forceCloseMap) {
        Object.keys(fieldsMap).forEach(filterType => {
          if (forceCloseMap[filterType]) {
            fieldsMap[filterType].isFilterShown = false
          }
        })
      }

      if (viewMode !== prevProps.viewMode) {
        const { filterStoresByTypeMap } = prevProps.store
        Object.keys(filterStoresByTypeMap).forEach(filterType => {
          filterStoresByTypeMap[filterType].clickOnCancel()
        })

        dynamicOverflowListStore.enableRecalculation()
      }
    })
  }

  public componentWillUnmount() {
    this.clearPostEventCallback()
  }

  public render() {
    return (
      <MenuCloser
        className="tw:flex tw:items-center tw:[&>*]:flex-1 tw:h-full logistic-filter tw:justify-start tw:relative tw:basis-[300px] tw:grow"
        closeMenu={this.hideOverflowedFiltersModal}
        isOpen={this.isOverflowedModalShown}
      >
        {this.renderHandleButtons()}
        {this.renderPopups()}
      </MenuCloser>
    )
  }

  private renderPopups() {
    return Object.keys(this.storesMap).map(filterType => {
      const store = this.storesMap[filterType]
      return <SuperFilter key={filterType} filterStore={store} />
    })
  }

  private renderHandleButtons = (): JSX.Element => {
    return (
      <DynamicOverflowList
        items={Object.keys(this.storesMap).map(filterType => (
          <SuperFilterHandleButton
            className="tw:grow-0"
            key={filterType}
            filterStore={this.storesMap[filterType]}
            shouldRenderRightIcon
            hasIndent={false}
          />
        ))}
        store={this.localProps.dynamicOverflowListStore}
        elementsClassName="tw:align-middle tw:pl-2.5"
        moreButtonWidth={MORE_BTN_WIDTH}
        minContainerWidthConstraint={MIN_CONTAINER_WIDTH}
      >
        {({
          visibleElements,
          overflowElements,
          containerRefSetter,
          setAdditionalElementsRef,
        }) => (
          <div
            className={classList({
              'overflowed-filters-list': true,
              'tw:whitespace-nowrap': visibleElements.length === 1,
            })}
            ref={containerRefSetter}
          >
            {visibleElements}
            <div className="tw:inline-block tw:align-middle tw:pl-2.5">
              {this.renderOverflowedFiltersLabel(overflowElements)}
            </div>
            <div
              className="tw:inline-block tw:pl-[5px] tw:align-middle"
              ref={setAdditionalElementsRef}
            >
              <FilterResetter onReset={this.resetAllFilters} />
            </div>
          </div>
        )}
      </DynamicOverflowList>
    )
  }

  @computed
  private get hasActiveOverflowedFilter(): boolean {
    const { numberOfVisibleElements } = this.localProps.dynamicOverflowListStore
    const filterTypes = Object.keys(this.storesMap)
    return filterTypes
      .slice(numberOfVisibleElements)
      .some(type => this.storesMap[type].isHandleButtonActive)
  }

  private renderOverflowedFiltersLabel = (
    hiddenElements: JSX.Element[],
  ): JSX.Element => {
    if (!hiddenElements?.length) {
      return null
    }

    return (
      <>
        <FacetButton
          label="..."
          isOpen={this.isOverflowedModalShown}
          isActive={this.hasActiveOverflowedFilter}
          onClick={this.toggleOverflowedFiltersModal}
          showChevron={false}
        />
        {this.isOverflowedModalShown && (
          <div className="tw:absolute tw:flex-1 tw:flex tw:flex-col tw:p-2.5 tw:z-[100] tw:bg-popover tw:mt-2.5 tw:border tw:border-solid tw:border-border">
            {hiddenElements.map(item => (
              <div key={item.key} className="tw:p-[5px]">
                {item}
              </div>
            ))}
          </div>
        )}
      </>
    )
  }

  @computed
  private get storesMap() {
    return this.localProps.store.filterStoresByTypeMap
  }

  private resetAllFilters = () => {
    this.localProps.store.resetAllFilters()
    this.localProps.onResetClick?.()
  }

  private handleDesktopPostEventCallback = (eventContext: EventContext) => {
    const [eventType] = eventContext.event

    if (RESET_ALL_FILTERS === eventType) {
      Object.keys(this.storesMap).forEach(filterType => {
        const filterStore = this.storesMap[filterType]

        filterStore.clickOnDeselectAll()
        filterStore.clickOnApply()
      })

      this.hideOverflowedFiltersModal()
    }
  }

  @action.bound
  private toggleOverflowedFiltersModal() {
    this.isOverflowedModalShown = !this.isOverflowedModalShown
  }

  @action.bound
  private hideOverflowedFiltersModal() {
    this.isOverflowedModalShown = false
  }
}
